<template>
  <article data-page-name="legal">
    <div class="content">
      <aside v-if="contentTableNavItems?.length" class="left">
        <strong>{{ $t('Table_of_Contents') }}</strong>

        <nav>
          <ul>
            <li
              v-for="{ href, title } of contentTableNavItems"
              :key="href"
              :class="{ active: href === activeId }"
            >
              <NuxtLink v-html="title" :to="href" />
            </li>
          </ul>
        </nav>
      </aside>

      <div class="middle">
        <p class="last-update">
          <span>Last update: </span>

          <time :datetime="post.updated_at">
            {{ $dayjs(post.updated_at).format('MMMM DD, YYYY') }}
          </time>
        </p>

        <h1 v-if="post?.data?.title">
          {{ post.data.title }}
        </h1>

        <div
          v-if="post?.data?.content"
          v-html="post.data.content"
          class="markup"
        />
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { showError } from '#app';

// composables
import { useActiveSectionObserver } from '~/composables/useActiveSectionObserver';

// stores
import useUIStore from '~/stores/ui';

// utils
import get from 'lodash.get';

// types
import { PostStatus, type ContentTableNavItem } from '~/types';

const { setLanguageSelectorVisibility } = useUIStore();

const {
  params: { slug },
  query: { preview },
} = useRoute();

const { error, data: post } = await useAsyncGql({
  operation: 'BlogArticle',
  variables: {
    slug: slug as string,
    status: preview ? undefined : PostStatus.PUBLISHED,
  },
  options: {
    transform: data => data.blog_article,
  },
});

if (error.value || !post.value) {
  showError(
    `Failed to fetch data. Operation: ${error.value?.operationName}; StatusCode: ${error.value?.statusCode}`,
  );
}

const contentTableNavItems = ref<ContentTableNavItem[]>([]);

const { activeId } = useActiveSectionObserver(contentTableNavItems);

if (import.meta.client) {
  watch(
    post,
    postValue => {
      if (!postValue || !Array.isArray(postValue.data?.content_table)) return;

      contentTableNavItems.value = postValue.data.content_table
        .filter(item => item?.title && item?.href)
        .map(item => ({
          ...item,
          distanceFromTop: null,
          listener: null,
        }));
    },
    {
      immediate: true,
    },
  );
}

useSeoMeta({
  title: get(post.value, 'data.seo.title'),
  ogTitle: get(post.value, 'data.seo.title'),
  description: get(post.value, 'data.seo.description'),
  ogDescription: get(post.value, 'data.seo.description'),
  ogImage: get(post.value, 'data.seo.image'),
  ogType: get(post.value, 'data.seo.og_type'),
});

setLanguageSelectorVisibility(false);
onUnmounted(() => setLanguageSelectorVisibility(true));
</script>

<style scoped lang="scss">
@import '$/mixins/flex';
@import '$/mixins/typography';
@import '$/mixins/media';
@import '$/mixins/size';
@import '$/variables/fonts';
@import '$/variables/colors';
@import '$/variables/shadows';
@import '$/functions/token';

[data-page-name='legal'] {
  @include wrapper(56.625rem);

  &:has(aside) {
    @include wrapper(80rem);
  }

  position: relative;

  padding: 8rem 2rem 2rem 2rem;

  @include tablet {
    padding: 8rem 2rem 2rem 2rem;
  }

  @include mobile {
    padding: 4rem 1rem 2rem 1rem;
  }

  .content {
    @include flex-start-sb;
    gap: 0 2rem;

    @include media(900px) {
      flex-direction: column;
    }

    aside.left {
      position: sticky;
      top: calc(var(--scroll-offset) + 1rem);

      @include fixed-width(17.5rem);

      @include media(900px) {
        position: static;
        @include fluid-max-width(100%);
        margin-bottom: 2.5rem;

        @include fixed-width(100%);
      }

      strong {
        @include subtitle-2;

        display: block;
        margin-bottom: 1rem;
      }

      ul {
        margin-bottom: 1rem;

        @include media(900px) {
          margin-bottom: 0;
        }

        li {
          a {
            transition: background-color 0.15s ease;

            display: block;
            padding: 0.5rem 1rem;

            &:not(:has(strong)),
            &:has(strong):deep(strong) {
              color: token('text-secondary');
              @include body-3;
            }
          }

          &.active {
            a {
              &:not(:has(strong)),
              &:has(strong):deep(strong) {
                color: token('text-primary');
                @include subtitle-3;
              }
            }
          }

          &:hover {
            a {
              background-color: rgba(token('accent-hover'), 16%);
            }
          }
        }
      }
    }

    .middle {
      width: 100%;

      p.last-update {
        text-align: center;
        @include subtitle-2;

        @include tablet {
          text-align: left;
        }

        span {
          text-transform: uppercase;
          color: token('text-secondary');
        }

        time {
          text-transform: capitalize;
        }
      }

      h1 {
        @include h2;
        padding: 1.5rem 0rem;
      }

      :deep(.markup) {
        h3,
        h2,
        h4,
        p {
          padding-bottom: 1.5rem;
        }

        h2 {
          @include h3;
        }

        h3 {
          @include h4;
        }

        p {
          @include body-2;
          padding-bottom: 1.25rem;

          word-break: break-word;
        }

        a {
          @include link-text-2;
          color: token('link');
        }

        ol {
          @include body-2;
        }

        ul {
          @include body-2;
          margin-bottom: 1rem;

          li {
            margin-left: 1rem;
            position: relative;

            &:before {
              position: absolute;

              @include fixed-size(4px);

              background-color: token('text-primary');
              border-radius: 50%;
              content: '';
              display: block;

              top: 12px;
              left: -12px;
            }
          }
        }

        figure.table {
          width: 100%;
          margin-left: 0;
          margin-right: 0;

          overflow-x: auto;

          table {
            border-spacing: 0;
            border: 0;
            max-height: max-content;
            width: 100%;
            height: auto;

            font-family: map-get($fonts, 'Inter');
            font-size: 1.125rem;
          }

          th,
          td {
            box-sizing: border-box;
            padding: 1rem 0.5rem;
          }

          thead {
            color: token('text-primary');
            @include subtitle-3;

            tr {
              th {
                border-bottom: 2px solid token('outline-primary');
              }
            }
          }

          tbody {
            th,
            td {
              @include body-3;
              border-bottom: 1px solid token('outline-secondary');
            }
          }
        }
      }
    }
  }
}
</style>
